// src/pages/channel_partners/ChannelPartners.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Container,
  Tooltip,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  BusinessCenter as BusinessCenterIcon,
  Email, Phone
} from '@mui/icons-material';
import {  } from "@mui/icons-material";
import { Stack } from "@mui/material";

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import { useModal } from '../../contexts/ModalContext';
import { useChannelPartner } from '../../contexts/ChannelPartnerContext';
import ChannelPartnerForm from './ChannelPartnerForm';
import ChannelPartnerDeleteModal from './ChannelPartnerDeleteModal';

const ChannelPartners = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { openSuccessModal, openErrorModal, openConfirmModal } = useModal();
  const { 
    channelPartners, 
    loading, 
    fetchChannelPartners, 
    createChannelPartner, 
    updateChannelPartner, 
    deleteChannelPartner,
    updateChannelPartnerStatus
  } = useChannelPartner();

  // Local state management
  const [formLoading, setFormLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [deletingPartner, setDeletingPartner] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState({});

  // Load channel partners on component mount
  useEffect(() => {
    const loadChannelPartners = async () => {
      try {
        await fetchChannelPartners();
      } catch (error) {
        enqueueSnackbar('Error loading channel partners', { variant: 'error' });
      }
    };
    loadChannelPartners();
  }, []);

  // Create or update channel partner
  const handleFormSubmit = async (data, editingPartner) => {
    try {
      setFormLoading(true);
      
      if (editingPartner) {
        await updateChannelPartner({ ...data, id: editingPartner.id });
        openSuccessModal('Success', 'Channel partner updated successfully');
      } else {
        await createChannelPartner(data);
        openSuccessModal('Success', 'Channel partner created successfully');
      }
      
      setOpenFormModal(false);
      setEditingPartner(null);
    } catch (error) {
      console.error('Error saving channel partner:', error);
      enqueueSnackbar(error.message || 'Error saving channel partner', { variant: 'error' });
    } finally {
      setFormLoading(false);
    }
  };

  // Delete channel partner
  const handleDelete = async (deletingPartner) => {
    if (!deletingPartner) return;

    try {
      setDeleteLoading(true);
      await deleteChannelPartner(deletingPartner.id);
      openSuccessModal('Success', 'Channel partner deleted successfully');
      setOpenDeleteModal(false);
      setDeletingPartner(null);
    } catch (error) {
      console.error('Error deleting channel partner:', error);
      enqueueSnackbar(error.message || 'Error deleting channel partner', { variant: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Open edit modal
  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setOpenFormModal(true);
  };

  // Open add modal
  const handleAdd = () => {
    setEditingPartner(null);
    setOpenFormModal(true);
  };

  // Open delete confirmation modal
  const handleDeleteClick = (partner) => {
    setDeletingPartner(partner);
    setOpenDeleteModal(true);
  };

  // Close form modal
  const handleCloseFormModal = () => {
    setOpenFormModal(false);
    setEditingPartner(null);
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setDeletingPartner(null);
  };

  // Handle status change
  const handleStatusChange = async (partnerId, newStatus) => {
    try {
      setStatusUpdating(prev => ({ ...prev, [partnerId]: true }));
      await updateChannelPartnerStatus(partnerId, newStatus);
      openSuccessModal('Success', 'Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      openErrorModal('Error', error.message || 'Error updating status');
    } finally {
      setStatusUpdating(prev => ({ ...prev, [partnerId]: false }));
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'error';
  };

  // Format contact info
  const formatContactInfo = (partner) => {
    const parts = [];
  
    if (partner.email) {
      parts.push(
        <Stack key="email" direction="row" alignItems="center" spacing={0.5}>
          <Email fontSize="tiny" sx={{ color: 'primary.main' }} /> 
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            {partner.email}
          </Typography>
        </Stack>
      );
    }
  
    if (partner.phone) {
      parts.push(
        <Stack key="phone" direction="row" alignItems="center" spacing={0.5}>
          <Phone fontSize="tiny" sx={{ color: 'success.info' }} />
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            {partner.country_code ? partner.country_code : ''}{partner.phone}
          </Typography>
        </Stack>
      );
    }
  
    // Vertical stack instead of horizontal
    return (
      <Stack direction="column" spacing={0.5}>
        {parts}
      </Stack>
    );
  };
  


  return (
    <Container 
      maxWidth={false} 
      disableGutters
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        height: '100vh',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        px: 2,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
          }}
        >
        </Box>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={handleAdd}
          size="small"
          sx={{ 
            textTransform: 'none', 
            borderRadius: '5px',
            py: 0.7,
            boxShadow: 'none',
            backgroundColor: '#007050', 
            '&:hover': { backgroundColor: '#005a3f' }
          }}
        >
          Add New
        </Button>
      </Box>

      {/* Content Area */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : channelPartners.length === 0 ? (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 0,
              flexDirection: 'column',
              px: 2,
              m: 2
            }}
          >
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No Channel Partners Found
            </Typography>
            <Button 
              variant="outlined" 
              startIcon={<AddIcon />} 
              onClick={handleAdd}
              size="small"
              sx={{ mt: 1, textTransform: 'none' }}
            >
              Add Channel Partner
            </Button>
          </Box>
        ) : (
          <TableContainer 
            component={Paper} 
            elevation={0}
            sx={{ 
              border: 'none',
              borderRadius: 0,
              flex: 1,
              height: '100%'
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 500, py: 1.5, px: 2 }}>Channel Partner ID</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 1.5, px: 2 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 1.5, px: 2 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 1.5, px: 2 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 1.5, px: 2 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 1.5, px: 2 }}>Leads Count</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 1.5, px: 2 }}>Notes Count</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 1.5, px: 2 }}>Created</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500, py: 1.5, px: 2 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {channelPartners.map((partner) => (
                  <TableRow 
                    key={partner.id}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: 'rgba(0, 0, 0, 0.04)' 
                      } 
                    }}
                  >
                    <TableCell sx={{ py: 1.5, px: 2 }}>
                      <Box
                        onClick={() => navigate(`/channel-partners/${partner.id}`)}
                        sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 700,
                            color: 'info.main',
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            display: 'block',
                            mb: 0.5,
                            letterSpacing: '0.5px'
                          }}
                        >
                          {partner.channel_partner_id? `#${partner.channel_partner_id}` : 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, px: 2 }}>
                      <Box
                        onClick={() => navigate(`/channel-partners/${partner.id}`)}
                        sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '0.9rem',
                            color: 'text.primary',
                            fontWeight: 500
                          }}
                        >
                          {partner.name || 'Unnamed Partner'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, px: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {partner.email || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, px: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {partner.phone || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, px: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={partner.status}
                          onChange={(e) => handleStatusChange(partner.id, e.target.value)}
                          disabled={statusUpdating[partner.id]}
                          sx={{
                            '& .MuiSelect-select': {
                              py: 0.5,
                              px: 1,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: partner.status === 'active' ? '#2e7d32' : '#d32f2f',
                              backgroundColor: partner.status === 'active' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                              borderRadius: 1,
                              border: 'none'
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              border: 'none'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              border: '1px solid',
                              borderColor: partner.status === 'active' ? '#4caf50' : '#f44336'
                            }
                          }}
                        >
                          <MenuItem value="active" sx={{ fontSize: '0.75rem' }}>
                            Active
                          </MenuItem>
                          <MenuItem value="inactive" sx={{ fontSize: '0.75rem' }}>
                            Inactive
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, px: 2 }}>
                      <Typography variant="body2">
                        {partner.leads_count || 0}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, px: 2 }}>
                      <Typography variant="body2">
                        {partner.notes_count || 0}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, px: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {partner.created_at}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.5, px: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon sx={{ fontSize: '0.9rem' }} />}
                            onClick={() => handleEdit(partner)}
                            sx={{
                              borderColor: '#4caf50',
                              color: '#2e7d32',
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              fontSize: '0.75rem',
                              '&:hover': {
                                borderColor: '#388e3c',
                                backgroundColor: 'rgba(76, 175, 80, 0.04)'
                              },
                              '&:disabled': {
                                borderColor: '#e0e0e0',
                                color: '#9e9e9e'
                              }
                            }}
                          >
                            Edit
                          </Button>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DeleteIcon sx={{ fontSize: '0.9rem' }} />}
                            onClick={() => handleDeleteClick(partner)}
                            sx={{
                              borderColor: '#f44336',
                              color: '#d32f2f',
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              fontSize: '0.75rem',
                              '&:hover': {
                                borderColor: '#c62828',
                                backgroundColor: 'rgba(76, 175, 80, 0.04)'
                              },
                              '&:disabled': {
                                borderColor: '#e0e0e0',
                                color: '#9e9e9e'
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Form Modal */}
      <ChannelPartnerForm
        open={openFormModal}
        onClose={handleCloseFormModal}
        onSubmit={(data) => handleFormSubmit(data, editingPartner)}
        editingPartner={editingPartner}
        loading={formLoading}
      />

      {/* Delete Confirmation Modal */}
      <ChannelPartnerDeleteModal
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={() => handleDelete(deletingPartner)}
        deletingPartner={deletingPartner}
        loading={deleteLoading}
      />

    </Container>
  );
};

export default ChannelPartners;